import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { useMultiAccount, Account } from '../../hooks/useMultiAccount';
import { usePortalLocationOptional } from '../../contexts/PortalLocationContext';
import LocationSwitcher from '../Portal/LocationSwitcher';
import './PortalHeader.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PortalHeader: React.FC = () => {
  const { user, logout } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dropdown states
  const [ordersMenuOpen, setOrdersMenuOpen] = useState(false);
  const [billingMenuOpen, setBillingMenuOpen] = useState(false);
  const [farmMenuOpen, setFarmMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // Refs for click-outside handling
  const ordersMenuRef = useRef<HTMLDivElement>(null);
  const billingMenuRef = useRef<HTMLDivElement>(null);
  const farmMenuRef = useRef<HTMLDivElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  // Multi-account support
  const { accounts, hasMultipleAccounts } = useMultiAccount();
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // Multi-location support (optional - only for retail partners with multiple locations)
  const locationContext = usePortalLocationOptional();
  const isRetailPartner = user?.customer?.customer_type === 'RETAIL_PARTNER';
  const showLocationSwitcher = isRetailPartner && locationContext && !locationContext.loading;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ordersMenuRef.current && !ordersMenuRef.current.contains(event.target as Node)) {
        setOrdersMenuOpen(false);
      }
      if (billingMenuRef.current && !billingMenuRef.current.contains(event.target as Node)) {
        setBillingMenuOpen(false);
      }
      if (farmMenuRef.current && !farmMenuRef.current.contains(event.target as Node)) {
        setFarmMenuOpen(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isOrdersActive = () => {
    return isActive('/portal/orders') || isActive('/portal/quotes') || isActive('/portal/place-order');
  };

  const isBillingActive = () => {
    return isActive('/portal/billing') || isActive('/portal/tax-exemption');
  };

  const isFarmActive = () => {
    return isActive('/portal/farmhand') || location.pathname.startsWith('/portal/farmhand') ||
           isActive('/portal/farmmap') || isActive('/portal/farm-tasks') ||
           location.pathname.startsWith('/portal/reports') ||
           location.pathname.startsWith('/portal/tools');
  };

  const isAccountActive = () => {
    if (isRetailPartner) {
      return isActive('/portal/team') || isActive('/portal/resources') ||
             location.pathname.startsWith('/portal/resources') ||
             isActive('/portal/my-documents') ||
             location.pathname.startsWith('/portal/reports') ||
             isActive('/portal/settings') || isActive('/portal/email-preferences') ||
             location.pathname.startsWith('/portal/support');
    }
    return isActive('/portal/farm-team') || isActive('/portal/settings') ||
           isActive('/portal/email-preferences') || isActive('/portal/my-documents') ||
           isActive('/portal/resources') || location.pathname.startsWith('/portal/support');
  };

  const closeAllDropdowns = () => {
    setOrdersMenuOpen(false);
    setBillingMenuOpen(false);
    setFarmMenuOpen(false);
    setAccountMenuOpen(false);
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    closeAllDropdowns();
  };

  const handleSwitchAccount = async (account: Account) => {
    setSwitchingAccount(true);
    setSwitchError(null);

    try {
      const body: any = {
        account_type: account.type,
      };

      if (account.portal_user_id) {
        body.portal_user_id = account.portal_user_id;
      }
      if (account.member_id) {
        body.member_id = account.member_id;
      }

      const response = await fetch(`${API_BASE_URL}/auth/universal-login/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.user_type === 'portal') {
          localStorage.setItem('portal_user', JSON.stringify(data.user));
          localStorage.removeItem('bridge_club_member');
        } else if (data.user_type === 'bridge_club') {
          localStorage.setItem('bridge_club_member', JSON.stringify(data.member));
          localStorage.removeItem('portal_user');
        }

        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (account.type === 'bridge_club') {
          window.location.href = '/bridge-club/dashboard';
        } else {
          window.location.href = '/portal/dashboard';
        }
      } else {
        setSwitchError(data.error || 'Failed to switch account');
      }
    } catch (err: any) {
      setSwitchError('Network error. Please try again.');
    } finally {
      setSwitchingAccount(false);
    }
  };

  // Render dropdown menu component
  const renderDropdown = (
    label: string,
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
    ref: React.RefObject<HTMLDivElement>,
    isActiveCheck: () => boolean,
    items: { to: string; label: string; condition?: boolean }[]
  ) => {
    const filteredItems = items.filter(item => item.condition !== false);
    if (filteredItems.length === 0) return null;

    return (
      <div className="portal-nav-dropdown" ref={ref}>
        <button
          className={`portal-nav-link portal-nav-dropdown-trigger ${isActiveCheck() ? 'active' : ''}`}
          onClick={() => {
            closeAllDropdowns();
            setIsOpen(!isOpen);
          }}
        >
          {label}
          <span className={`portal-dropdown-arrow ${isOpen ? 'open' : ''}`}>▾</span>
        </button>
        {isOpen && (
          <div className="portal-dropdown-menu">
            {filteredItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                className={`portal-dropdown-item ${isActive(item.to) || location.pathname.startsWith(item.to + '/') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="portal-header">
      <div className="portal-header-container">
        <div className="portal-header-left">
          <div className="portal-header-branding">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="portal-header-logo" />
            <div className="portal-header-brand-text">
              <div className="portal-header-company">Red Bridge Farm Feed Co.</div>
              <div className="portal-header-portal-label">Customer Portal</div>
            </div>
          </div>
          {/* Location Switcher for multi-location retail partners */}
          {showLocationSwitcher && (
            <div className="portal-header-location-switcher">
              <LocationSwitcher />
            </div>
          )}
          {user && (
            <nav className={`portal-header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {/* Crew members get simplified navigation */}
              {user.permissions.farm_role === 'crew' ? (
                <>
                  <Link
                    to="/portal/crew-dashboard"
                    className={`portal-nav-link ${isActive('/portal/crew-dashboard') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/portal/farm-tasks"
                    className={`portal-nav-link ${isActive('/portal/farm-tasks') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    Tasks
                  </Link>
                  <Link
                    to="/portal/farmmap"
                    className={`portal-nav-link ${isActive('/portal/farmmap') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    Farm Map
                  </Link>
                </>
              ) : (
                <>
                  {/* Dashboard - always visible */}
                  <Link
                    to="/portal/dashboard"
                    className={`portal-nav-link ${isActive('/portal/dashboard') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    Dashboard
                  </Link>

                  {/* Orders Dropdown */}
                  {renderDropdown(
                    'Orders',
                    ordersMenuOpen,
                    setOrdersMenuOpen,
                    ordersMenuRef,
                    isOrdersActive,
                    [
                      { to: '/portal/orders', label: 'My Orders' },
                      { to: '/portal/quotes', label: 'Quotes' },
                      { to: '/portal/place-order', label: 'Place Order', condition: user.permissions.can_place_orders },
                    ]
                  )}

                  {/* Billing Dropdown */}
                  {renderDropdown(
                    'Billing',
                    billingMenuOpen,
                    setBillingMenuOpen,
                    billingMenuRef,
                    isBillingActive,
                    [
                      { to: '/portal/billing', label: 'Pay My Bill' },
                      { to: '/portal/tax-exemption', label: 'Tax Exemption' },
                    ]
                  )}

                  {/* Retail Partners vs Wholesale Farms - Different navigation */}
                  {isRetailPartner ? (
                    <>
                      {/* Bridge Club - prominent link for retail partners */}
                      <Link
                        to="/portal/bridge-club"
                        className={`portal-nav-link ${isActive('/portal/bridge-club') || location.pathname.startsWith('/portal/bridge-club') ? 'active' : ''}`}
                        onClick={handleNavClick}
                      >
                        Bridge Club
                      </Link>

                      {/* Account Dropdown (Retail) */}
                      {renderDropdown(
                        'Account',
                        accountMenuOpen,
                        setAccountMenuOpen,
                        accountMenuRef,
                        isAccountActive,
                        [
                          { to: '/portal/team', label: 'Team' },
                          { to: '/portal/resources', label: 'Resources' },
                          { to: '/portal/my-documents', label: 'My Documents' },
                          { to: '/portal/reports', label: 'Reports' },
                          { to: '/portal/api-integration', label: 'API Integration' },
                          { to: '/portal/settings', label: 'Settings' },
                          { to: '/portal/support', label: 'Help & Support' },
                        ]
                      )}
                    </>
                  ) : (
                    <>
                      {/* Farmhand (Wholesale) — sub-items hidden while we
                          rebuild as Farmer's Partner (Feb 2027). Now a single
                          top-level link to the coming-soon page. The dropdown
                          + sub-routes are preserved in code for the rebuild. */}
                      <Link
                        to="/portal/farmhand"
                        className={`portal-nav-link ${isFarmActive() ? 'active' : ''}`}
                        onClick={handleNavClick}
                      >
                        Farmhand
                      </Link>

                      {/* Account Dropdown (Wholesale) */}
                      {renderDropdown(
                        'Account',
                        accountMenuOpen,
                        setAccountMenuOpen,
                        accountMenuRef,
                        isAccountActive,
                        [
                          { to: '/portal/farm-team', label: 'Farm Team', condition: user.permissions.can_manage_farm_employees || user.permissions.farm_role === 'farm_owner' },
                          { to: '/portal/resources', label: 'Resources' },
                          { to: '/portal/my-documents', label: 'My Documents' },
                          { to: '/portal/settings', label: 'Settings' },
                          { to: '/portal/support', label: 'Help & Support' },
                        ]
                      )}
                    </>
                  )}
                </>
              )}
            </nav>
          )}
        </div>
        {user && (
          <div className="portal-header-right">
            <div className="portal-header-user-info">
              <div className="portal-header-welcome">Welcome,</div>
              <div className="portal-header-username">{user.name}</div>
              <div className="portal-header-customer">{user.customer.display_name || user.customer.name}</div>
            </div>
            <div className="portal-header-actions">
              {hasMultipleAccounts && (
                <button
                  onClick={() => setShowAccountSwitcher(true)}
                  className="portal-btn-switch"
                  title="Switch to another account"
                >
                  <span className="switch-icon">⇄</span>
                  <span className="switch-text">Switch</span>
                </button>
              )}
              <button onClick={handleLogout} className="portal-btn-logout">
                Logout
              </button>
            </div>
            {/* Hamburger Menu Button */}
            <button
              className={`portal-hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <span className="portal-hamburger-line"></span>
              <span className="portal-hamburger-line"></span>
              <span className="portal-hamburger-line"></span>
            </button>
          </div>
        )}
      </div>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="portal-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Account Switcher Modal */}
      {showAccountSwitcher && (
        <div className="account-switcher-overlay" onClick={() => setShowAccountSwitcher(false)}>
          <div className="account-switcher-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="account-switcher-close"
              onClick={() => setShowAccountSwitcher(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 className="account-switcher-title">Switch Account</h2>
            <p className="account-switcher-subtitle">
              Select an account to switch to:
            </p>

            <div className="account-switcher-list">
              {accounts.map((account, index) => (
                <button
                  key={index}
                  className={`account-switcher-option ${account.is_current ? 'current' : ''}`}
                  onClick={() => !account.is_current && handleSwitchAccount(account)}
                  disabled={switchingAccount || account.is_current}
                >
                  <span className="account-switcher-icon">
                    {account.icon || (account.type === 'bridge_club' ? '🌾' : '🏪')}
                  </span>
                  <div className="account-switcher-info">
                    <div className="account-switcher-label">
                      {account.label}
                      {account.is_current && <span className="current-badge">Current</span>}
                    </div>
                    <div className="account-switcher-desc">{account.subtitle}</div>
                  </div>
                  {!account.is_current && <span className="account-switcher-arrow">→</span>}
                </button>
              ))}
            </div>

            {switchingAccount && (
              <p className="account-switcher-loading">Switching account...</p>
            )}

            {switchError && (
              <div className="account-switcher-error">{switchError}</div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default PortalHeader;
